import { useState, useCallback } from 'react';
import cx from 'classnames';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faPalette,
  faCode,
  faRotateLeft,
} from '@fortawesome/pro-regular-svg-icons';

import {
  useAppDispatch,
  useAppSelector,
  preferencesSelector,
  customCssSelector,
  mapSelector,
  updatePreference,
  setCustomCss,
  savedCoordinatesSelector,
  PreferencesType,
  LabelStyle,
  DEFAULT_LABEL_STYLE,
  allowLabelsSelector,
} from '../../store';

import { CssEditorModal } from './CssEditorModal';
import {
  getLabelBadgeClassName,
  getLabelBadgeStyleVars,
  LABEL_VARIANT_OPTIONS,
  resolveLabelVariant,
} from '../labels/shared/labelBadge';

import './ThemeSidebar.scss';
import '../labels/shared/LabelBadge.scss';

interface ThemeSidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ToggleSwitchProps {
  label: string;
  description?: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

function ToggleSwitch({ label, description, checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <label className={cx("theme-toggle", { "theme-toggle--disabled": disabled })}>
      <div className="theme-toggle__text">
        <span className="theme-toggle__label">{label}</span>
        {description && (
          <span className="theme-toggle__description">{description}</span>
        )}
      </div>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={`Toggle ${label}`}
        className={`theme-toggle__control ${checked ? 'theme-toggle__control--on' : ''}`}
        onClick={() => onChange(!checked)}
        disabled={disabled}
      >
        <span className="theme-toggle__knob" />
      </button>
    </label>
  );
}

interface SelectOptionProps<T extends string> {
  label: string;
  description?: string;
  value: T;
  options: { value: T; label: string }[];
  onChange: (value: T) => void;
  disabled?: boolean;
}

function SelectOption<T extends string>({ label, description, value, options, onChange, disabled }: SelectOptionProps<T>) {
  return (
    <label className={cx("theme-select", { "theme-select--disabled": disabled })}>
      <div className="theme-select__text">
        <span className="theme-select__label">{label}</span>
        {description && (
          <span className="theme-select__description">{description}</span>
        )}
      </div>
      <select
        aria-label={label}
        value={value}
        onChange={(e) => onChange(e.target.value as T)}
        className="theme-select__control"
        disabled={disabled}
      >
        {options.map((opt) => (
          <option key={opt.value} value={opt.value}>
            {opt.label}
          </option>
        ))}
      </select>
    </label>
  );
}

interface ColorPickerProps {
  label: string;
  description?: string;
  value?: string;
  defaultValue: string;
  onChange: (value: string) => void;
}

interface TextInputProps {
  label: string;
  description?: string;
  value: string;
  placeholder?: string;
  onChange: (value: string) => void;
}

function ColorPicker({ label, description, value, defaultValue, onChange }: ColorPickerProps) {
  return (
    <label className="theme-color">
      <div className="theme-color__text">
        <span className="theme-color__label">
          {label}
          {value && value !== defaultValue && (
            <span title="Reset to default">
              &nbsp;
              <FontAwesomeIcon
                icon={faRotateLeft}
                onClick={(e) => {
                  onChange(defaultValue);
                  e.preventDefault();
                }}
              />
            </span>
          )}
        </span>
        {description && (
          <span className="theme-color__description">{description}</span>
        )}
      </div>
      <div className="theme-color__control">
        <input
          type="color"
          value={value || defaultValue}
          onChange={(e) => onChange(e.target.value)}
        />
        <span className="theme-color__value">{value || defaultValue}</span>
      </div>
    </label>
  );
}

function TextInput({ label, description, value, placeholder, onChange }: TextInputProps) {
  return (
    <label className="theme-select">
      <div className="theme-select__text">
        <span className="theme-select__label">{label}</span>
        {description && (
          <span className="theme-select__description">{description}</span>
        )}
      </div>
      <input
        type="text"
        value={value}
        placeholder={placeholder}
        onChange={(event) => onChange(event.target.value)}
        className="theme-select__control"
      />
    </label>
  );
}

interface DefaultLabelStyleSectionProps {
  defaultLabelStyle?: LabelStyle;
  defaultLabelRotation?: number;
  onChange: (style: LabelStyle) => void;
  onChangeRotation: (rotation: number) => void;
}

function DefaultLabelStyleSection(props: DefaultLabelStyleSectionProps) {
  const {
    defaultLabelStyle,
    defaultLabelRotation,
    onChange,
    onChangeRotation,
  } = props;
  const style: LabelStyle = { ...DEFAULT_LABEL_STYLE, ...defaultLabelStyle };
  const rotation: number = defaultLabelRotation ?? 0;

  const update = (partial: Partial<LabelStyle>) => {
    onChange({ ...style, ...partial });
  };
  const updateRotation = (newRotation: number) => {
    onChangeRotation(newRotation);
  };
  // To adjust margins based on rotation:
  // const getLabelPreviewMargin = (rotation: number) => {
  //   // Interpolates between 20px (0/180 deg) and 50px (90/-90 deg)
  //   return 20 + 30 * Math.abs(Math.sin((rotation * Math.PI) / 180));
  // };
  // const margin = getLabelPreviewMargin(rotation);
  // const labelPreviewStyle = {
  //   marginTop: `${margin}px`,
  //   marginBottom: `${margin}px`,
  // };
  const labelPreviewStyle = { marginTop: '50px', marginBottom: '50px' };

  return (
    <section className="theme-sidebar__section">
      <h4>Default Label Style</h4>
      <div className="theme-sidebar__label-preview" style={ labelPreviewStyle }>
        <span
          className={getLabelBadgeClassName(style.variant)}
          style={getLabelBadgeStyleVars(style, rotation) as React.CSSProperties}
        >
          Label Preview
        </span>
      </div>
      <SelectOption
        label="Style preset"
        value={resolveLabelVariant(style.variant)}
        options={LABEL_VARIANT_OPTIONS}
        onChange={(value) => update({ variant: value })}
      />
      <label className="theme-select">
        <span className="theme-select__label">Font size</span>
        <input type="number" min={8} max={72} value={style.fontSize} className="theme-select__control"
          onChange={(e) => update({ fontSize: Number(e.target.value) })} />
      </label>
      <label className="theme-toggle">
        <span className="theme-toggle__label">Bold</span>
        <input type="checkbox" checked={style.fontWeight === 'bold'}
          onChange={(e) => update({ fontWeight: e.target.checked ? 'bold' : 'normal' })} />
      </label>
      <label className="theme-color">
        <span className="theme-color__label">Text colour</span>
        <input type="color" value={style.color} onChange={(e) => update({ color: e.target.value })} />
      </label>
      <label className="theme-color">
        <span className="theme-color__label">Background colour</span>
        <input type="color" value={style.backgroundColor} onChange={(e) => update({ backgroundColor: e.target.value })} />
      </label>
      <label className="theme-select">
        <span className="theme-select__label">Background opacity</span>
        <input type="range" min={0} max={1} step={0.05} value={style.backgroundOpacity}
          onChange={(e) => update({ backgroundOpacity: Number(e.target.value) })} />
      </label>
      <label className="theme-color">
        <span className="theme-color__label">Border colour</span>
        <input type="color" value={style.borderColor === 'transparent' ? '#333333' : style.borderColor}
          onChange={(e) => update({ borderColor: e.target.value })} />
      </label>
      <label className="theme-select">
        <span className="theme-select__label">Padding</span>
        <input type="number" min={0} max={40} value={style.padding} className="theme-select__control"
          onChange={(e) => update({ padding: Number(e.target.value) })} />
      </label>
      <label className="theme-select">
        <span className="theme-select__label">Default rotation (°)</span>
        <input type="number" min={-180} max={180} value={rotation} className="theme-select__control"
          onChange={(e) => updateRotation(Number(e.target.value))} />
      </label>
    </section>
  );
}

export function ThemeSidebar({ isOpen, onClose }: ThemeSidebarProps) {
  const dispatch = useAppDispatch();
  const preferences = useAppSelector(preferencesSelector);
  const customCss = useAppSelector(customCssSelector);
  const iMap = useAppSelector(mapSelector);
  const savedCoordinates = useAppSelector(savedCoordinatesSelector);
  const allowLabels = useAppSelector(allowLabelsSelector);

  const [cssModalOpen, setCssModalOpen] = useState(false);

  const handlePreferenceChange = useCallback(<K extends keyof PreferencesType>(
    key: K,
    value: PreferencesType[K]
  ) => {
    dispatch(updatePreference({ key, value }));
  }, [dispatch]);

  const handleCssChange = useCallback((value: string) => {
    dispatch(setCustomCss(value));
  }, [dispatch]);

  if (!isOpen) return null;

  return (
    <div className="theme-sidebar">
      <header className="theme-sidebar__header">
        <div className="theme-sidebar__title">
          <FontAwesomeIcon icon={faPalette} />
          <h3>Theme Editor</h3>
        </div>
        <button
          className="theme-sidebar__close"
          onClick={onClose}
          aria-label="Close theme editor"
        >
          <FontAwesomeIcon icon={faTimes} />
        </button>
      </header>

      <div className="theme-sidebar__content">
        <section className="theme-sidebar__section">
          <h4>Brand Colors</h4>

          <ColorPicker
            label="Primary Color"
            description="Main brand color for buttons and accents"
            value={preferences.primaryColor}
            defaultValue={iMap.primaryColor}
            onChange={(value) => handlePreferenceChange('primaryColor', value)}
          />

          <ColorPicker
            label="Secondary Color"
            description="Secondary accent color"
            value={preferences.secondaryColor}
            defaultValue={iMap.secondaryColor}
            onChange={(value) => handlePreferenceChange('secondaryColor', value)}
          />

          <ColorPicker
            label="Background Color"
            description="Container background color"
            value={preferences.bgColor}
            defaultValue={preferences.darkMode ? '#1a1a2e' : iMap.bgColor}
            onChange={(value) => handlePreferenceChange('bgColor', value)}
          />
        </section>

        <section className="theme-sidebar__section">
          <h4>Appearance</h4>

          <ToggleSwitch
            label="Dark Mode"
            description="Use dark theme throughout"
            checked={preferences.darkMode}
            onChange={(checked) => {
              handlePreferenceChange('darkMode', checked);
              handlePreferenceChange('bgColor', checked ? '#1a1a2e' : iMap.bgColor);
            }}
          />

          <ToggleSwitch
            label="Compact Mode"
            description="Reduce spacing for smaller screens"
            checked={preferences.compactMode}
            onChange={(checked) => handlePreferenceChange('compactMode', checked)}
          />
        </section>

        <section className="theme-sidebar__section">
          <h4>Layout Options</h4>

          <ToggleSwitch
            label="Show Sidebar"
            description="Display navigation sidebar to users"
            checked={preferences.sidebarEnabled}
            onChange={(checked) => handlePreferenceChange('sidebarEnabled', checked)}
          />

          <SelectOption
            label="Sidebar Position"
            value={preferences.sidebarPosition}
            options={[
              { value: 'left', label: 'Left' },
              { value: 'right', label: 'Right' },
            ]}
            onChange={(value) => handlePreferenceChange('sidebarPosition', value)}
            disabled={!preferences.sidebarEnabled}
          />

          <ToggleSwitch
            label="Sidebar Open on Load"
            description="Start with sidebar expanded on desktop"
            checked={preferences.sidebarOpenOnLoad}
            onChange={(checked) => handlePreferenceChange('sidebarOpenOnLoad', checked)}
            disabled={!preferences.sidebarEnabled}
          />

          <ToggleSwitch
            label="Sidebar Open on Mobile"
            description="Start with sidebar expanded on mobile devices"
            checked={preferences.sidebarOpenOnLoadMobile}
            onChange={(checked) => handlePreferenceChange('sidebarOpenOnLoadMobile', checked)}
            disabled={!preferences.sidebarEnabled}
          />

          <ToggleSwitch
            label="Show Zoom Controls"
            description="Display zoom buttons on the map"
            checked={preferences.showZoomControls}
            onChange={(checked) => handlePreferenceChange('showZoomControls', checked)}
          />

          <SelectOption
            label="Zoom Control Position"
            description="Where zoom buttons appear"
            value={preferences.zoomControlPosition}
            options={([
              { value: 'topleft' as const, label: 'Top Left' },
              { value: 'topright' as const, label: 'Top Right' },
              { value: 'bottomleft' as const, label: 'Bottom Left' },
              { value: 'bottomright' as const, label: 'Bottom Right' },
            ]).filter((opt) =>
              // Exclude positions that overlap with sidebar burger or layer switcher
              opt.value !== 'bottomleft' &&
              opt.value !== (preferences.sidebarPosition === 'right' ? 'topright' : 'topleft')
            )}
            onChange={(value) => handlePreferenceChange('zoomControlPosition', value)}
            disabled={!preferences.showZoomControls}
          />

          <ToggleSwitch
            label="Fill Viewport"
            description="Zoom to fill screen on load (may crop edges)"
            checked={preferences.fillViewport}
            onChange={(checked) => handlePreferenceChange('fillViewport', checked)}
          />
        </section>

        <section className="theme-sidebar__section">
          <h4>Marker Interaction</h4>

          <SelectOption
            label="Popup Style"
            description="How marker details are displayed"
            value={preferences.popupStyle}
            options={[
              { value: 'popup', label: 'Popup (Default)' },
              { value: 'card', label: 'Card (Elegant Overlay)' },
            ]}
            onChange={(value) => handlePreferenceChange('popupStyle', value)}
          />
        </section>

        <section className="theme-sidebar__section">
          <h4>Weather Panel</h4>

          <TextInput
            label="Weather Coordinates"
            description="Latitude and longitude shown in the weather panel"
            value={preferences.weatherCoords ?? ''}
            placeholder="53.3228, -6.2254"
            onChange={(value) => handlePreferenceChange('weatherCoords', value)}
          />

          {!preferences.weatherCoords?.trim() && (
            <p className="theme-sidebar__weather-note">
              'No coordinates set yet. Add latitude and longitude to enable the weather panel.'
            </p>
          )}
          <div className="theme-sidebar__weather-button">
            {savedCoordinates && savedCoordinates !== preferences.weatherCoords && (
              <button
                type="button"
                onClick={() => {
                  handlePreferenceChange('weatherCoords', savedCoordinates);
                }}
              >
                Use Map Coordinates
              </button>
            )}
          </div>
        </section>

        {preferences.sidebarEnabled !== undefined && allowLabels && (
          <DefaultLabelStyleSection
            defaultLabelStyle={preferences.defaultLabelStyle}
            defaultLabelRotation={preferences.defaultLabelRotation}
            onChange={(style) => handlePreferenceChange('defaultLabelStyle', style)}
            onChangeRotation={(rotation) => handlePreferenceChange('defaultLabelRotation', rotation)}
          />
        )}

        <section className="theme-sidebar__section theme-sidebar__section--css">
          <h4>Custom CSS</h4>
          <button
            type="button"
            className="theme-sidebar__css-button"
            onClick={() => setCssModalOpen(true)}
          >
            <FontAwesomeIcon icon={faCode} />
            <span>Edit Custom CSS</span>
            {customCss && <span className="theme-sidebar__css-indicator" />}
          </button>
          <p className="theme-sidebar__css-help">
            Add custom CSS to override default styles.
          </p>
        </section>
      </div>

      <CssEditorModal
        isOpen={cssModalOpen}
        onClose={() => setCssModalOpen(false)}
        value={customCss}
        onChange={handleCssChange}
      />
    </div>
  );
}

export default ThemeSidebar;
